// MACHINERY V2 (Stroje) — debounced fleet search (M2.2, review finding F5).
//
// Split out of FleetV1Page to keep it under the 300-line cap, and because the
// two bugs this fixes are subtle enough to deserve their own file.
//
// BUG 1 — STALE MERGE BASE. `changeQuery` REPLACES the query wholesale
// (customerMachinerySlice.ts:150-152). The old debounced callback closed over
// `query` at KEYSTROKE time, so typing, then picking a type filter inside the
// 400 ms window, fired the timer with the pre-filter snapshot and silently
// reverted the filter the user had just chosen. `queryRef` keeps the merge base
// current without re-creating the callback (and without re-arming the timer)
// every time the query changes.
//
// BUG 2 — TIMER OUTLIVING THE PAGE, WITH CROSS-SURFACE REACH. `query` lives in
// the SHARED `customerMachinery` slice, which the LIVE /machinery grid also
// reads. A pending timer that fired after this page unmounted wrote a stale
// `search` into that shared query — and the live grid's `useEffect([query])`
// then fetched with it. A v2 page could therefore leave the live surface
// filtered by a search the user had abandoned. Clearing on unmount is not
// tidiness; it is the fix.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { MachinesQuery } from "../../models/models";
import { changeQuery } from "../../customerMachinerySlice";

export interface FleetSearch {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  /** Always the CURRENT query — safe to read inside async handlers that were
   *  created earlier (duplicate / restore refreshes use it too). */
  queryRef: MutableRefObject<MachinesQuery>;
}

export const useFleetSearch = (query: MachinesQuery): FleetSearch => {
  const dispatch = useAppDispatch();
  const [searchValue, setSearchValue] = useState<string>(query.search || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryRef = useRef<MachinesQuery>(query);
  queryRef.current = query;

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const commit = useCallback(
    (value: string) => {
      dispatch(changeQuery({ ...queryRef.current, search: value.trim() || undefined }));
    },
    [dispatch]
  );

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => commit(value), 400);
    },
    [commit]
  );

  const onSearchSubmit = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    commit(searchValue);
  }, [commit, searchValue]);

  return { searchValue, onSearchChange, onSearchSubmit, queryRef };
};

export default useFleetSearch;
